import React, { useState, useEffect, useRef, useMemo } from "react";
import Link from "gatsby-link";

export const Search = ({ pages, onClose }) => {
  const [query, setQuery] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Detect and observe dark mode from body class (browser only)
    if (typeof window === "undefined" || !window.document || !window.document.body) {
      return;
    }

    const body = window.document.body;

    const updateDarkMode = () => {
      setIsDarkMode(body.classList.contains("dark-mode"));
    };

    // Initial detection
    updateDarkMode();

    // Observe changes to the body's class attribute to react to theme toggles
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          updateDarkMode();
          break;
        }
      }
    });

    observer.observe(body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    if (inputRef.current) {
      inputRef.current.focus();
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Memoize searchable items - only rebuild when pages change, not on every keystroke
  const searchableItems = useMemo(() => {
    const items = [];

    // Add all pages
    pages.forEach(page => {
      items.push({
        ...page,
        searchName: page.name.toLowerCase(),
        searchDescription: (page.description || "").toLowerCase(),
        isField: false
      });

      // If it's a query or mutation page, add individual fields
      if ((page.type === "QUERY" || page.type === "MUTATION") && page.fields) {
        page.fields.forEach(field => {
          items.push({
            name: field.name,
            description: field.description,
            path: `${page.path}#${field.name}`,
            type: page.type,
            kind: page.type,
            parentName: page.name,
            searchName: field.name.toLowerCase(),
            searchDescription: (field.description || "").toLowerCase(),
            isField: true
          });
        });
      }
    });

    return items;
  }, [pages]);

  // Memoize filtered results - group by type and prioritize
  const results = useMemo(() => {
    if (!query.trim()) {
      return {};
    }

    const searchQuery = query.toLowerCase();

    // Search through all items
    const allMatches = searchableItems
      .filter(item => {
        return item.searchName.includes(searchQuery) ||
               item.searchDescription.includes(searchQuery);
      });

    // Group results by type with priority order
    const groups = {
      queries: [],
      mutations: [],
      objects: [],
      other: []
    };

    allMatches.forEach(item => {
      if (item.type === "QUERY") {
        groups.queries.push(item);
      } else if (item.type === "MUTATION") {
        groups.mutations.push(item);
      } else if (item.kind === "OBJECT") {
        groups.objects.push(item);
      } else {
        groups.other.push(item);
      }
    });

    // Sort within each group alphabetically and limit results
    Object.keys(groups).forEach(key => {
      groups[key] = groups[key]
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 10); // Limit each section
    });

    return groups;
  }, [query, searchableItems]);

  // Memoize total result count to avoid redundant calculations
  const totalResults = useMemo(() => {
    return Object.values(results).reduce((total, items) => total + items.length, 0);
  }, [results]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const getSectionHeader = (key) => {
    switch (key) {
      case 'queries': return 'Queries';
      case 'mutations': return 'Mutations';
      case 'objects': return 'Objects';
      case 'other': return 'Other Types';
      default: return key;
    }
  };

  const formatParentName = (parentName) => {
    // Validate input
    if (!parentName || typeof parentName !== 'string') {
      return 'Unknown';
    }

    // Remove common suffixes from the parent name
    const cleanedName = parentName.replace(/(Queries|Mutations)$/, '');
    return cleanedName;
  };

  const getTypeLabel = (page) => {
    if (page.type === "QUERY") return "Query";
    if (page.type === "MUTATION") return "Mutation";
    if (page.kind === "OBJECT") return "Object";
    if (page.kind === "INPUT_OBJECT") return "Input";
    if (page.kind === "ENUM") return "Enum";
    if (page.kind === "UNION") return "Union";
    if (page.kind === "SCALAR") return "Scalar";
    return "Type";
  };

  const getTypeBadgeColor = (page) => {
    if (page.type === "QUERY") return "#10b981";
    if (page.type === "MUTATION") return "#f59e0b";
    return "#4d65ff";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "100px"
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: isDarkMode ? "#21262d" : "#ffffff",
          borderRadius: "8px",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "500px",
          display: "flex",
          flexDirection: "column"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "20px", borderBottom: `1px solid ${isDarkMode ? "#30363d" : "#e5e7eb"}` }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search queries, mutations, types..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: "1.6rem",
              border: `1px solid ${isDarkMode ? "#30363d" : "#e5e7eb"}`,
              borderRadius: "6px",
              outline: "none",
              fontFamily: "inherit",
              backgroundColor: isDarkMode ? "#0d1117" : "#ffffff",
              color: isDarkMode ? "#c9d1d9" : "#000000"
            }}
          />
        </div>
        <div
          style={{
            overflowY: "auto",
            flexGrow: 1
          }}
        >
          {Object.keys(results).length === 0 && query.trim() && (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: isDarkMode ? "#8b949e" : "#6b6b6b",
                fontSize: "1.4rem"
              }}
            >
              No results found for "{query}"
            </div>
          )}
          {Object.keys(results).length === 0 && !query.trim() && (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: isDarkMode ? "#8b949e" : "#6b6b6b",
                fontSize: "1.4rem"
              }}
            >
              Start typing to search...
            </div>
          )}
          {Object.entries(results)
            .filter(([key, items]) => items.length > 0)
            .map(([sectionKey, sectionResults]) => (
              <div key={sectionKey}>
                <div
                  style={{
                    padding: "12px 20px 8px",
                    fontSize: "1.2rem",
                    fontWeight: "700",
                    color: isDarkMode ? "#8b949e" : "#6b6b6b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    backgroundColor: isDarkMode ? "#0d1117" : "#f9fafb",
                    borderBottom: `1px solid ${isDarkMode ? "#21262d" : "#e5e7eb"}`
                  }}
                >
                  {getSectionHeader(sectionKey)}
                </div>
                {sectionResults.map((result) => (
                  <Link
                    key={result.path}
                    to={result.path}
                    onClick={onClose}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 20px",
                      textDecoration: "none",
                      color: isDarkMode ? "#c9d1d9" : "#1a1a1a",
                      borderBottom: `1px solid ${isDarkMode ? "#30363d" : "#f3f4f6"}`,
                      transition: "background-color 0.2s ease"
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isDarkMode ? "#161b22" : "#f9fafb")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <span
                      style={{
                        backgroundColor: getTypeBadgeColor(result),
                        color: "#ffffff",
                        fontSize: "1.1rem",
                        padding: "3px 8px",
                        borderRadius: "4px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        marginRight: "12px",
                        minWidth: "65px",
                        textAlign: "center"
                      }}
                    >
                      {getTypeLabel(result)}
                    </span>
                    <div style={{ flexGrow: 1 }}>
                      <div style={{ fontWeight: "600", fontSize: "1.5rem" }}>
                        {result.name}
                        {result.isField && result.parentName && (
                          <span style={{ fontWeight: "400", color: "#6b6b6b", fontSize: "1.3rem" }}>
                            {" in "}
                            {formatParentName(result.parentName)}
                          </span>
                        )}
                      </div>
                      {result.description && (
                        <div
                          style={{
                            fontSize: "1.3rem",
                            color: "#6b6b6b",
                            marginTop: "2px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {result.description}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ))}
        </div>
        <div
          style={{
            padding: "12px 20px",
            borderTop: `1px solid ${isDarkMode ? "#30363d" : "#e5e7eb"}`,
            backgroundColor: isDarkMode ? "#161b22" : "#f9fafb",
            fontSize: "1.2rem",
            color: isDarkMode ? "#8b949e" : "#6b6b6b",
            display: "flex",
            justifyContent: "space-between"
          }}
        >
          <span>Press <kbd style={{ backgroundColor: isDarkMode ? "#21262d" : "#ffffff", padding: "2px 6px", borderRadius: "3px", border: `1px solid ${isDarkMode ? "#30363d" : "#e5e7eb"}` }}>ESC</kbd> to close</span>
          <span>
            {totalResults > 0 && (
              `${totalResults} result${totalResults === 1 ? "" : "s"}`
            )}
          </span>
        </div>
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/Search.jsx